import { ErrorHandler, Injectable } from '@angular/core';
import { getFromStorage, removeFromStorage, setToStorage } from '@shared/utility/session-local-storage';
import { StorageKeysEnum } from '@shared/utility/storage-keys';

@Injectable()
export class AppErrorHandlerService implements ErrorHandler {
  private readonly reloadTime = getFromStorage<number>('localStorage', StorageKeysEnum.chunkLoadErrorCount);
  private reloading = false;

  public handleError(error: any): void {
    const msg = this.stringify(error);

    // intercetta i fallimenti dei chunk/dynamic import
    if (/dynamically imported module/i.test(msg)) {
      this.reloadSafely(error);
      return;
    }

    // warning benigno del browser: la callback ResizeObserver cambia il layout e le notifiche
    // restanti vengono rinviate al frame successivo (non perse). Lo scatena il relayout di Bryntum
    // sul cambio di raggruppamento della timeline; niente si rompe, quindi lo silenziamo.
    if (/ResizeObserver loop/i.test(msg)) {
      return;
    }

    // l'estensione Angular DevTools tenta di agganciarsi anche fuori localhost, dove l'app gira in
    // modalità di produzione (niente `ng.getComponent`/API di debug): l'aggancio fallisce sempre,
    // senza alcun impatto funzionale. Lo silenziamo per non sporcare i log applicativi.
    if (/Angular DevTools/i.test(msg)) {
      return;
    }

    // richiesta fetch annullata (AbortController): keycloak-js aggiorna il token prima di ogni
    // richiesta e la fetch viene abortita su navigazione, cambio di scheda del browser o token superato da uno nuovo.
    // È un annullamento atteso, non un errore: lo silenziamo per non sporcare i log.
    if (error?.name === 'AbortError' || /user aborted a request/i.test(msg)) {
      return;
    }

    // altro: lascia il log standard
    // (puoi integrare Sentry/console/etc.)
    console.error(error);
  }

  private stringify(err: any): string {
    if (err?.message) {
      return String(err.message);
    }
    if (typeof err === 'string') {
      return err;
    }
    try {
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }

  private async reloadSafely(error: any) {
    if (this.reloading) {
      return;
    }

    // Evito loop di reload
    const reloadTime = this.reloadTime() ?? 0;
    if (reloadTime >= 3) {
      console.warn('[ChunkReloadFail] reload già effettuato.');
      console.error(error);
      return;
    }
    this.reloading = true;
    this.incrementChunkLoadErrorCount();
    setTimeout(() => {
      removeFromStorage('localStorage', StorageKeysEnum.chunkLoadErrorCount);
    }, 15000);

    try {
      // svuota CacheStorage del browser (non tocca localStorage/cookie)
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* ignore */
    } finally {
      location.reload(); // reload «pulito»
    }
  }

  private incrementChunkLoadErrorCount(): void {
    setToStorage('localStorage', StorageKeysEnum.chunkLoadErrorCount, (this.reloadTime() ?? 0) + 1);
  }
}
